import { useEffect } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";

import type { WizardDraftStepsByType, WizardStep, WizardType } from "./types";
import { saveWizardStepDraft } from "./wizardDraftStorage";

export const WIZARD_DRAFT_DEBOUNCE_MS = 400;

interface UseWizardStepDraftOptions<
  TWizard extends WizardType,
  TStep extends WizardStep<TWizard>,
  TValues extends WizardDraftStepsByType[TWizard][TStep] & FieldValues,
> {
  form: Pick<UseFormReturn<TValues>, "subscribe">;
  wizard: TWizard;
  entityId?: string;
  step: TStep;
  enabled?: boolean;
  debounceMs?: number;
  transformValues?: (values: TValues) => TValues;
}

export function useWizardStepDraft<
  TWizard extends WizardType,
  TStep extends WizardStep<TWizard>,
  TValues extends WizardDraftStepsByType[TWizard][TStep] & FieldValues,
>({
  form,
  wizard,
  entityId,
  step,
  enabled = true,
  debounceMs = WIZARD_DRAFT_DEBOUNCE_MS,
  transformValues,
}: UseWizardStepDraftOptions<TWizard, TStep, TValues>): void {
  useEffect(() => {
    if (!enabled || !entityId) {
      return;
    }

    let pendingValues: TValues | null = null;
    let saveTimeout: ReturnType<typeof setTimeout> | null = null;

    const flush = () => {
      if (!pendingValues) {
        return;
      }

      if (saveTimeout) {
        clearTimeout(saveTimeout);
        saveTimeout = null;
      }

      saveWizardStepDraft(wizard, entityId, step, pendingValues);
      pendingValues = null;
    };

    const unsubscribe = form.subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        pendingValues = transformValues ? transformValues(values) : values;

        if (saveTimeout) {
          clearTimeout(saveTimeout);
        }

        saveTimeout = setTimeout(flush, Math.max(0, debounceMs));
      },
    });

    window.addEventListener("pagehide", flush);

    return () => {
      unsubscribe();
      window.removeEventListener("pagehide", flush);
      if (saveTimeout) {
        clearTimeout(saveTimeout);
      }
    };
  }, [debounceMs, enabled, entityId, form, step, transformValues, wizard]);
}
